import { motion } from "framer-motion";
import { useLanguage } from "../context/LanguageContext";
import PageHero from "../components/PageHero";

const CLIENTS = [
  { name: "CJR", img: "/Partners_logos/CJR_logo.png" },
  { name: "Infratec", img: "/Partners_logos/INFRATEC_logo.png" },
  { name: "Omexom", img: "/Partners_logos/OMEXOM_logo.png" },
  { name: "Tecneira", img: "/Partners_logos/TECNEIRA_logo.png" },
  { name: "CME", img: "/Partners_logos/CME_logo.png" },
  { name: "LessKW", img: "/Partners_logos/LESSKW_logo.png" },
  { name: "GreenVenture", img: "/Partners_logos/GREENVENTURE_logo.png" },
  { name: "Helexia", img: "/Partners_logos/HELEXIA_logo.png" },
  { name: "Hyperion", img: "/Partners_logos/HYPERION_logo.png" },
  { name: "Sesenergia", img: "/Partners_logos/SESENERGIA_logo.png" },
  { name: "Zircon", img: "/Partners_logos/ZIRCON_logo.png" },
  { name: "DST", img: "/Partners_logos/DST_logo.png" },
  { name: "RME", img: "/Partners_logos/RME_logo.png" },
  { name: "Gesto", img: "/Partners_logos/GESTO_logo.png" },
  { name: "Eurowind", img: "/Partners_logos/EUROWIND_logo.png" },
  { name: "Sisacol", img: "/Partners_logos/SISACOL_logo.png" },
  { name: "Isosigma", img: "/Partners_logos/ISOSIGMA_logo.png" },
];

const CO_CLIENTS = [
  { name: "Amarenco", img: "/Partners_logos/AMARENCO_logo.png" },
  { name: "Aquila", img: "/Partners_logos/AQUILA_logo.png" },
  { name: "Greenvolt", img: "/Partners_logos/GREENVOLT_logo.png" },
  { name: "Proef", img: "/Partners_logos/PROEF_logo.png" },
  { name: "Triplewatt", img: "/Partners_logos/TRIPLEWATT_logo.png" },
  { name: "Neoene", img: "/Partners_logos/NEOENE_logo.png" },
  { name: "ANA", img: "/Partners_logos/ANA_logo.png" },
  { name: "Aeroporto Dubai", img: "/Partners_logos/AEROPORTO_DUBAI_logo.png" },
  { name: "Etihad", img: "/Partners_logos/ETIHAD_logo.png" },
  { name: "Altri", img: "/Partners_logos/ALTRI_logo.png" },
  { name: "ASA", img: "/Partners_logos/ASA_logo.png" },
  { name: "AFRAS", img: "/Partners_logos/AFRAS_logo.png" },
  { name: "Hospital da Luz", img: "/Partners_logos/HOSPITAL_DA_LUZ_logo.png" },
];

const TRANSLATIONS = {
  en: {
    heroBadge: "Clients",
    gridBadge: "Our clients",
    title: "Trusted by Leaders",
    subtitle: "We collaborate with industry leaders across Europe to deliver cutting-edge engineering and commissioning solutions.",
    coBadge: "OWNERS / FINAL CLIENTS",
  },
  pt: {
    heroBadge: "Clientes",
    gridBadge: "Os nossos clientes",
    title: "A Escolha dos Líderes",
    subtitle: "Colaboramos com líderes do setor em toda a Europa para fornecer soluções de engenharia e comissionamento de ponta.",
    coBadge: "CLIENTES FINAIS",
  }
};

const containerVariants = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1
    }
  }
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  show: { opacity: 1, y: 0, transition: { type: "spring", stiffness: 300, damping: 24 } }
};

export default function ProjectsClients() {
  const { language } = useLanguage();
  const t = TRANSLATIONS[language];

  return (
    <div className="min-h-screen bg-[#0A0A0A] isolate">
      <PageHero
        eyebrow={t.heroBadge}
        title={
          language === "pt" ? (
            <>A Escolha dos <span className="text-[#F68A31]">Líderes</span>.</>
          ) : (
            <>Trusted by <span className="text-[#F68A31]">Leaders</span>.</>
          )
        }
        subtitle={t.subtitle}
        image="https://images.pexels.com/photos/6814523/pexels-photo-6814523.jpeg"
      />

      <div className="relative z-10 mx-auto max-w-[1400px] px-6 py-16 md:px-12 lg:px-16">
        
        {/* --- ADICIONADO: SECÇÃO: CLIENTES PRINCIPAIS --- */}
        <div className="mb-12 max-w-4xl">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="flex items-center gap-4 mb-4"
          >
            <div className="h-[1px] w-8 bg-[#F68A31]"></div>
            <span className="text-[#F68A31] text-sm font-semibold tracking-widest uppercase">
              {t.gridBadge}
            </span>
          </motion.div>
        </div>

        {/* Grelha de Clientes Principais */}
        <motion.div 
          variants={containerVariants}
          initial="hidden"
          animate="show"
          className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 md:gap-6"
        >
          {CLIENTS.map((client, index) => (
            <motion.div
              key={index}
              variants={itemVariants}
              className="group relative flex h-32 items-center justify-center rounded-2xl border border-white/5 bg-white/[0.02] p-6 transition-all duration-500 hover:border-[#F68A31]/50"
            >
              <img
                src={client.img}
                alt={`Logo ${client.name}`}
                className="max-h-full max-w-full object-contain opacity-85 transition-all duration-500 group-hover:opacity-100 group-hover:scale-105"
                loading="lazy"
                decoding="async"
                draggable={false}
              />
            </motion.div>
          ))}
        </motion.div>

        {/* --- SECÇÃO: CO-PARTNERS --- */}
        <div className="mt-24 mb-12 max-w-4xl">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="flex items-center gap-4 mb-4"
          >
            <div className="h-[1px] w-8 bg-[#F68A31]"></div>
            <span className="text-[#F68A31] text-sm font-semibold tracking-widest uppercase">
              {t.coBadge}
            </span>
          </motion.div>
        </div>

        {/* Grelha de Co-Partners */}
        <motion.div 
          variants={containerVariants}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, margin: "-50px" }}
          className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 md:gap-6"
        >
          {CO_CLIENTS.map((client, index) => (
            <motion.div
              key={index}
              variants={itemVariants}
              className="group relative flex h-32 items-center justify-center rounded-2xl border border-white/5 bg-white/[0.02] p-6 transition-all duration-500 hover:border-[#F68A31]/50"
            >
              <img
                src={client.img}
                alt={`Logo ${client.name}`}
                className="max-h-full max-w-full object-contain opacity-85 transition-all duration-500 group-hover:opacity-100 group-hover:scale-105"
                loading="lazy"
                decoding="async"
                draggable={false}
              />
            </motion.div>
          ))}
        </motion.div>

      </div>
    </div>
  );
}